/* eslint-disable @typescript-eslint/naming-convention */
import React from "react";
import styled, { css } from "styled-components";
import { BaseLink, LinkProps as BaseLinkProps } from "./BaseLink";
import { Icon } from "./Icon";
import { style } from "./style";
export type ListItemProps = {
  label: string;
  className?: string;
  customLabel?: React.ReactNode;
  icon?: string;
  iconHover?: string;
  iconXlinkHrefPrefix?: string;
  customIcon?: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  danger?: boolean;
  selected?: boolean;
  selectedBackgroundColor?: string;
  selectIcon?: boolean;
  to?: string;
  isMobileNav?: boolean;
  isNested?: boolean;
  backgroundColor?: string;
  backgroundHoverColor?: string;
  labelColor?: string;
  labelHoverColor?: string;
  indent?: number;
  capitalize?: boolean;
  readOnly?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;
  onLeftIconClick?: React.MouseEventHandler<HTMLElement>;
  onTextAreaClick?: React.MouseEventHandler<HTMLElement>;
  onRightIconClick?: React.MouseEventHandler<HTMLElement>;
  forceExternal?: boolean;
  target?: "_self" | "_blank";
  hasExpandedLabels?: boolean;
} & Partial<BaseLinkProps>;

const ListItemTextWrapper = styled.div`
  flex-grow: 1;
  overflow: hidden;
`;

const ListItemLabelWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const ListItemLabel = styled.div<{
  isMobileNav?: boolean;
  labelColor?: string;
  capitalize?: boolean;
  expanded?: boolean;
}>`
  font-size: ${({ isMobileNav }) => (isMobileNav ? `1.25rem` : `1rem`)};
  color: ${({ labelColor }) => (labelColor ? `${labelColor}` : `${style.colors.grey.darkest}`)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ expanded }) =>
    expanded &&
    css`
      flex-grow: 1;
    `};
  ${({ capitalize }) =>
    capitalize &&
    css`
      text-transform: capitalize;
    `};
`;

const ListItemDescriptionSharedStyles = styled.div`
  font-size: 0.875rem;
  line-height: 1rem;
  color: ${style.colors.grey.dark};
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ListItemDescription = styled(ListItemDescriptionSharedStyles)``;
const ListItemDisabledReason = styled(ListItemDescriptionSharedStyles)``;

const ListItemIconWrapper = styled.div`
  align-self: stretch;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const ListItemIcon = styled(Icon)`
  margin-right: ${style.vars.space["2"]};
`;

const ListItemIconHover = styled(ListItemIcon)`
  margin-right: ${style.vars.space["2"]};
`;

const ListItemSelectedIcon = styled(Icon)`
  margin-right: ${style.vars.space["2"]};
  color: ${style.colors.white};
`;

const ListItemNestedArrowIcon = styled(Icon)<React.HTMLAttributes<HTMLElement>>`
  margin-right: ${style.vars.space["2"]};
  color: ${style.colors.grey.dark};
`;

const ListItemWrapper = styled.div<
  {
    disabled?: boolean;
    danger?: boolean;
    selected?: boolean;
    selectedBackgroundColor?: string;
    backgroundColor?: string;
    backgroundHoverColor?: string;
    labelHoverColor?: string;
    readOnly?: boolean;
    isMobileNav?: boolean;
    indent?: number;
  } & Partial<BaseLinkProps>
>`
  list-style-type: none;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
  background-color: ${({ backgroundColor }) => (backgroundColor ? `${backgroundColor}` : `${style.colors.white}`)};
  font-weight: ${style.vars.fontWeight.base};
  cursor: pointer;
  padding: ${({ isMobileNav }) => (isMobileNav ? `${style.vars.space["3"]}` : `${style.vars.space["2"]} ${style.vars.space["3"]}`)};
  ${({ indent }) =>
    indent &&
    css`
      padding-left: calc(${style.vars.space["3"]} + ${indent}rem);
    `}
  ${({ disabled, selected, backgroundHoverColor, labelHoverColor }) =>
    !disabled &&
    !selected &&
    css`
      &:hover {
        background-color: ${backgroundHoverColor
          ? `${backgroundHoverColor}`
          : /*
    we use important here, because the styling gets overridden
    by the ul li a rule
 */
            `${style.colors.grey.light}`} !important;
        color: ${labelHoverColor} !important;
        ${ListItemLabel} {
          font-weight: ${style.vars.fontWeight.bold};
        }
      }
      &:hover ${ListItemLabel} {
        font-weight: ${style.vars.fontWeight.bold};
        ${labelHoverColor &&
        css`
          color: ${labelHoverColor};
        `}
      }
      & ${ListItemIconHover} {
        display: none;
      }
      &:hover {
        & ${ListItemIconHover} + ${ListItemIcon} {
          display: none;
        }
        & ${ListItemIconHover} {
          display: block;
        }
      }
    `}
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: default;
      pointer-events: none;
      &:hover {
        color: initial;
      }
      ${ListItemIcon}, ${ListItemLabel}, ${ListItemDescription}, ${ListItemSelectedIcon} {
        opacity: 0.5;
      }
    `};
  ${({ selected, selectedBackgroundColor }) =>
    selected &&
    css`
      background: ${selectedBackgroundColor ? `${selectedBackgroundColor}` : `${style.colors.grey.dark}`};
      ${ListItemLabel} {
        color: ${style.colors.white};
        font-weight: ${style.vars.fontWeight.bold};
      }
      ${ListItemDescriptionSharedStyles} {
        color: ${style.colors.grey.light};
      }
      &:focus {
        outline-color: ${style.colors.white} !important;
      }
    `};
  ${({ danger }) =>
    danger &&
    css`
      ${ListItemLabel} {
        color: ${style.colors.danger.base};
      }
    `}

  ${({ readOnly }) =>
    readOnly &&
    css`
      pointer-events: none;
    `}

  &:focus {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};

    &:after {
      content: none !important;
    }
  }
`;

const StyledBaseLink = styled(BaseLink)`
  display: block;
  padding: 0 !important;

  &:focus,
  &:active {
    outline: ${style.vars.borderWidth.base} dotted ${style.colors.grey.dark};
    outline-offset: -${style.vars.whitespace.xsmall};
    &:after {
      content: none !important;
    }
  }

  ${ListItemWrapper}: {
    &:focus,
    &:active {
      outline: none;
    }
  } ;
`;

const ListItemLinkWrapper: React.FC<ListItemProps> = (props) => {
  const { to, className, forceExternal, target, onKeyDown } = props;
  return to ? (
    <StyledBaseLink to={to} className={className} forceExternal={forceExternal} target={target} tabIndex={0} onKeyDown={onKeyDown}>
      {props.children}
    </StyledBaseLink>
  ) : (
    <>{props.children}</>
  );
};

export const ListItem: React.FC<ListItemProps> = (props) => {
  const {
    className,
    disabled,
    danger,
    selected,
    selectedBackgroundColor,
    backgroundColor,
    backgroundHoverColor,
    labelHoverColor,
    to,
    onClick,
    forceExternal,
    label,
    hasExpandedLabels,
    customLabel,
    icon,
    iconHover,
    iconXlinkHrefPrefix,
    customIcon,
    description,
    disabledReason,
    selectIcon,
    isMobileNav,
    indent,
    isNested,
    labelColor,
    capitalize,
    readOnly,
    onLeftIconClick,
    onTextAreaClick,
    onRightIconClick,
    onKeyDown,
  } = props;

  const showDisabledReason = disabled && disabledReason;

  return (
    <ListItemLinkWrapper {...props}>
      <ListItemWrapper
        className={className}
        disabled={disabled}
        danger={danger}
        selected={selected}
        selectedBackgroundColor={selectedBackgroundColor}
        backgroundColor={backgroundColor}
        backgroundHoverColor={backgroundHoverColor}
        labelHoverColor={labelHoverColor}
        onClick={onClick}
        tabIndex={readOnly || to ? -1 : 0}
        forceExternal={forceExternal}
        isMobileNav={isMobileNav}
        indent={indent}
        onKeyDown={onKeyDown}
        readOnly={readOnly}
      >
        <ListItemIconWrapper onClick={onLeftIconClick}>
          {iconHover && <ListItemIconHover icon={iconHover} xlinkHrefPrefix={iconXlinkHrefPrefix} large />}
          {icon && <ListItemIcon icon={icon} xlinkHrefPrefix={iconXlinkHrefPrefix} large />}
          {customIcon}
        </ListItemIconWrapper>
        <ListItemTextWrapper onClick={onTextAreaClick}>
          <ListItemLabelWrapper>
            <ListItemLabel isMobileNav={isMobileNav} labelColor={labelColor} capitalize={capitalize} expanded={!!hasExpandedLabels}>
              {label}
            </ListItemLabel>
            {customLabel}
          </ListItemLabelWrapper>
          {description && !showDisabledReason && <ListItemDescription>{description}</ListItemDescription>}
          {showDisabledReason && <ListItemDisabledReason>{disabledReason}</ListItemDisabledReason>}
        </ListItemTextWrapper>
        <ListItemIconWrapper onClick={onRightIconClick}>
          {selected && selectIcon && <ListItemSelectedIcon icon="check"></ListItemSelectedIcon>}
          {isNested && <ListItemNestedArrowIcon icon="arrow-right"></ListItemNestedArrowIcon>}
        </ListItemIconWrapper>
      </ListItemWrapper>
    </ListItemLinkWrapper>
  );
};
